"use client";

// Documentation content — French. Mirrors public/documentation-fr.html.
// Édition concise : l'essentiel, la clé admin, rien d'autre.

import {
  Callout,
  DocBadge,
  DocTable,
  L,
  OL,
  P,
  type DocSection,
} from "./primitives";

export const sectionsFR: DocSection[] = [
  // ---- Partie 1 · Commencez ici ----
  {
    id: "what-is-homebook",
    num: "1.1",
    part: 1,
    title: "C'est quoi, HomeBook ?",
    body: (
      <>
        <P>
          HomeBook, c'est un calendrier familial partagé pour nos maisons. Chaque
          maison a sa page avec photos, prix et calendrier. Vous choisissez vos
          jours, les responsables disent oui ou non, et tout le monde voit qui
          dort où.
        </P>
        <P>
          Ça marche sur n'importe quel téléphone, tablette ou ordinateur — rien à
          installer.
        </P>
      </>
    ),
  },
  {
    id: "sign-in",
    num: "1.2",
    part: 1,
    title: "Connexion & accès admin",
    body: (
      <>
        <OL>
          <li>
            Ouvrez le site et appuyez sur <strong>Connexion</strong> (en haut à
            droite).
          </li>
          <li>
            Tapez le nom d'utilisateur et le mot de passe. Appuyez sur le bouton.
            Voilà.
          </li>
        </OL>
        <Callout tone="warn" label="Accès admin">
          <P>
            Nom d'utilisateur <code>admin</code> — mot de passe{" "}
            <code>{"admin" + "123"}</code>. L'admin peut tout faire et définir un
            nouveau mot de passe pour n'importe quel compte.{" "}
            <strong>Changez ce mot de passe</strong> avant que la famille
            utilise vraiment le site.
          </P>
        </Callout>
        <P>
          Pas encore de compte ? Un admin en crée un pour vous en moins d'une
          minute — ou utilisez le lien qu'il vous a envoyé.
        </P>
      </>
    ),
  },
  {
    id: "privacy",
    num: "1.3",
    part: 1,
    title: "Vos données restent privées",
    body: (
      <>
        <P>
          Les données personnelles (noms, adresses e-mail, numéros de téléphone)
          sont stockées chiffrées et ne sont <strong>jamais affichées en entier</strong>{" "}
          dans l'application. Vous verrez des formes partielles comme{" "}
          <code>Davy J.</code> ou <code>d•••@g•••.com</code>.
        </P>
        <Callout tone="info" label="Bon à savoir">
          <P>
            Pour modifier les vraies données d'une personne, un admin saisit à
            nouveau la valeur complète dans le formulaire de membre : une nouvelle
            valeur remplace l'ancienne.
          </P>
        </Callout>
      </>
    ),
  },

  // ---- Partie 2 · Le parcours principal ----
  {
    id: "how-it-works",
    num: "2.1",
    part: 2,
    title: "Comment ça marche",
    body: (
      <>
        <P>
          L'application démarre sur l'<strong>écran d'accueil</strong> : de
          grandes cartes, une par destination. Appuyez sur une carte pour ouvrir
          cette partie de l'application, et utilisez la flèche{" "}
          <strong>Retour</strong> (en haut à gauche) pour remonter d'un niveau —
          le logo HomeBook vous ramène toujours à l'accueil.
        </P>
        <OL>
          <li>Appuyez sur <strong>Parcourir les maisons</strong>, puis ouvrez une maison.</li>
          <li>
            Choisissez vos jours sur son calendrier — vert veut dire libre.
          </li>
          <li>
            Dites combien de personnes viennent, et choisissez{" "}
            <strong>Privé</strong> (toute la maison) ou <strong>Partagé</strong>.
          </li>
          <li>
            <strong>Un séjour privé peut être partagé entre membres :</strong>{" "}
            ajoutez les personnes qui viennent et le prix est divisé à parts
            égales entre elles (la part de chacun est visible sur le séjour).
            Une fois le séjour approuvé, la liste est verrouillée — seuls un
            admin ou le propriétaire peuvent encore la modifier.
          </li>
          <li>
            Envoyez la demande. Elle reste <strong>en attente</strong> jusqu'à ce
            que le propriétaire ou l'équipe l'approuve.
          </li>
          <li>
            Approuvée ? Votre nom apparaît sur ces jours et tout ce que vous avez
            réservé se trouve sous <strong>Mes voyages</strong>.
          </li>
        </OL>
        <Callout tone="note" label="Raccourci">
          <P>
            Vous pouvez aussi appuyer sur un jour vert du calendrier de votre{" "}
            <strong>Tableau de bord</strong> — la même fiche de réservation
            apparaît juste sous le calendrier.
          </P>
        </Callout>
      </>
    ),
  },
  {
    id: "calendar-colors",
    num: "2.2",
    part: 2,
    title: "Les couleurs du calendrier",
    body: (
      <>
        <DocTable
          head={["Couleur", "Signification"]}
          rows={[
            [
              <span key="g" className="inline-flex items-center gap-2">
                <span className="inline-block h-3 w-3 rounded-sm bg-emerald-500" />
                Vert
              </span>,
              "Libre. Vous pouvez demander ce jour.",
            ],
            [
              <span key="r" className="inline-flex items-center gap-2">
                <span className="inline-block h-3 w-3 rounded-sm bg-rose-500" />
                Rouge
              </span>,
              "Toute la maison est prise (réservation privée).",
            ],
            [
              <span key="b" className="inline-flex items-center gap-2">
                <span className="inline-block h-3 w-3 rounded-sm bg-sky-400" />
                Bleu
              </span>,
              "Séjour partagé — plusieurs personnes dans la maison.",
            ],
            [
              <span key="o" className="inline-flex items-center gap-2">
                <span className="inline-block h-3 w-3 rounded-sm border-2 border-dashed border-amber-500" />
                Orange
              </span>,
              "Demandé, en attente d'approbation.",
            ],
            [
              <span key="y" className="inline-flex items-center gap-2">
                <span className="inline-block h-3 w-3 rounded-sm bg-zinc-300" />
                Gris
              </span>,
              "La maison est fermée ce jour-là.",
            ],
            [
              <span key="d" className="inline-flex items-center gap-2">
                <span className="inline-block h-3 w-3 rounded-sm bg-zinc-400" />
                Gris foncé
              </span>,
              "Dans le passé — la journée est terminée.",
            ],
          ]}
        />
        <P>
          Appuyez sur n'importe quel jour pour voir qui s'y trouve. Les
          propriétaires et l'équipe ont les boutons <strong>Approuver</strong> /{" "}
          <strong>Rejeter</strong> pour les demandes en attente.
        </P>
      </>
    ),
  },
  {
    id: "my-trips",
    num: "2.3",
    part: 2,
    title: "Vos voyages & l'annulation",
    body: (
      <>
        <P>
          Tout ce que vous avez réservé se trouve sous <strong>Mes voyages</strong>.
          Une demande encore <strong>en attente</strong> peut être annulée par
          vous à tout moment ; un séjour <strong>approuvé</strong> est annulé par
          le propriétaire ou un admin — demandez-leur simplement.
        </P>
      </>
    ),
  },

  // ---- Partie 3 · Propriétaires, équipe & argent ----
  {
    id: "who-does-what",
    num: "3.1",
    part: 3,
    title: "Qui fait quoi",
    body: (
      <>
        <P>Chaque compte a un badge, et le badge décide ce qu'il peut faire :</P>
        <DocTable
          head={["Badge", "Peut faire"]}
          rows={[
            [
              <DocBadge key="v" tone="neutral">
                Visiteur
              </DocBadge>,
              "Seulement regarder les maisons.",
            ],
            [
              <DocBadge key="u" tone="neutral">
                Utilisateur
              </DocBadge>,
              "Réserver des maisons, voir ses voyages, payer les factures qui lui sont assignées.",
            ],
            [
              <DocBadge key="o" tone="violet">
                OWNER
              </DocBadge>,
              "Possède une ou plusieurs maisons : contrôle total, valide les demandes, voit leurs factures.",
            ],
            [
              <DocBadge key="m" tone="sky">
                MODERATOR
              </DocBadge>,
              "Un aide. Peut se voir confier une maison ou une permission pour valider les réservations.",
            ],
            [
              <DocBadge key="a" tone="dark">
                ADMIN
              </DocBadge>,
              "Tout : utilisateurs, rôles, maisons, réglages.",
            ],
          ]}
        />
      </>
    ),
  },
  {
    id: "manage-a-house",
    num: "3.2",
    part: 3,
    title: "Gérer une maison",
    body: (
      <>
        <P>
          Ouvrez une page de maison en tant que propriétaire ou admin : une{" "}
          <strong>barre de gestion</strong> apparaît en haut. Tout se modifie
          directement sur la page — pas d'admin séparé.
        </P>
        <L>
          <li>
            <strong>Modifier</strong> — titre, description, prix, remise famille.
          </li>
          <li>
            <strong>Disponibilité</strong> — les jours où la maison peut être
            réservée.
          </li>
          <li>
            <strong>Photos</strong> — ou cliquez simplement sur une image de la
            page pour la remplacer.
          </li>
          <li>
            <strong>Prix de saison</strong> — des tarifs plus élevés pour la
            haute saison.
          </li>
          <li>
            <strong>Masquer / Publier</strong> — retirer une maison de la liste
            publique et la remettre.
          </li>
        </L>
        <P>
          Les demandes en attente apparaissent sur la page de la maison et sur le
          calendrier de votre Tableau de bord — ouvrez le jour et appuyez sur{" "}
          <strong>Approuver</strong> ou <strong>Rejeter</strong>.
        </P>
      </>
    ),
  },
  {
    id: "house-staff",
    num: "3.3",
    part: 3,
    title: "Personnel & remises famille",
    body: (
      <>
        <P>
          Appuyez sur <strong>Personnel de la maison</strong> sur une page de
          maison pour y assigner un modérateur : il peut alors valider les
          réservations et gérer les factures de{" "}
          <strong>cette maison uniquement</strong> — il ne voit rien d'autre.
        </P>
        <P>
          Les maisons appartiennent aussi à des <strong>groupes
          familiaux</strong>. Les membres de la famille d'une maison paient
          automatiquement le prix réduit chez elle — vous choisissez le
          pourcentage en modifiant la maison.
        </P>
      </>
    ),
  },
  {
    id: "bills-and-money",
    num: "3.4",
    part: 3,
    title: "Factures et argent",
    body: (
      <>
        <P>
          Les coûts des maisons deviennent des <strong>factures</strong> : un
          numéro, un montant, et une personne qui doit payer. La page{" "}
          <strong>Dépenses</strong> garde tout en ordre :
        </P>
        <P>
          Le <strong>Tableau de bord</strong> suit le même filtre de maison :
          choisissez une maison (ou toutes) et chaque chiffre et chaque
          graphique suit — nuits par maison et par membre, dépenses par
          maison, payé par membre et la part de factures dont chaque membre
          est responsable.
        </P>
        <L>
          <li>
            Le <strong>sélecteur de maison</strong> en haut filtre chaque liste
            et chaque graphique par maison.
          </li>
          <li>
            Cliquez sur une carte de résumé (En retard, À payer sous peu, Payé)
            pour voir exactement ces factures.
          </li>
          <li>
            Chaque facture en attente montre une{" "}
            <strong>étiquette d'assignation</strong> — appuyez dessus pour
            choisir qui paie.
          </li>
          <li>
            <strong>Marquer payée</strong> enregistre qui a payé et quand ; les
            dépenses par mois et par catégorie sont graphées en dessous.
          </li>
        </L>
      </>
    ),
  },

  // ---- Partie 3 · Mots & solutions ----
  {
    id: "words-and-fixes",
    num: "3.5",
    part: 3,
    title: "Mots & solutions",
    body: (
      <>
        <DocTable
          head={["Mot sur le site", "Ce que ça veut dire"]}
          rows={[
            ["En attente", "Envoyé, en attente d'un oui."],
            ["Privé / Partagé", "Toute la maison pour vous / la partager avec d'autres."],
            ["Mes voyages", "Vos demandes et vos séjours."],
            ["Masquer / Publier", "Retirer une maison de / la remettre sur la liste publique."],
            ["Personnel de la maison", "Des modérateurs assignés à une maison."],
            ["Facture", "Une note : quoi, combien, qui paie."],
            ["Assignation", "La personne qui doit payer une facture."],
          ]}
        />
        <L>
          <li>
            <strong>Mot de passe oublié ?</strong> L'admin peut vous en définir
            un nouveau en quelques secondes.
          </li>
          <li>
            <strong>Un jour refuse de se choisir ?</strong> Il est pris, ou la
            maison est fermée. Essayez d'autres jours.
          </li>
          <li>
            <strong>Quelque chose a l'air cassé ?</strong> Quittez la page et
            revenez — ça répare la plupart des petits soucis.
          </li>
        </L>
      </>
    ),
  },
];
